/// <amd-module name="Wit/WorkItemTFVCLinks/Components/ChangesetPicker/ChangesetPreviewTable" />
import * as React from "react";
import { useMemo } from "react";
import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { Ago } from "VSSUI/Ago";
import { Persona, PersonaSize } from "VSSUI/Persona";
import { ColumnSelect, ITableColumn, renderSimpleCell, SimpleTableCell, Table } from "VSSUI/Table";
import { IListSelection } from "VSSUI/List";
import { TfvcChangesetRef } from "VC/Common/Contracts/Git";
import { getDateInUserTimeZone, tooltipTimeFormat } from "Wit/Common/DateUtils";
import * as Resources from "Wit/WorkItemTFVCLinks/Resources";

export interface IChangesetsPreviewTableProps {
    changesets: TfvcChangesetRef[];
    selection: IListSelection;
    loading: boolean;
}

// The table typings require an index signature even though it is not actually needed.
type ChangesetListItem = TfvcChangesetRef & Record<string, any>;

const numberOfPlaceholderRows = 5;
const placeholderItemProvider = new ObservableArray(
    new Array(numberOfPlaceholderRows).fill(new ObservableValue<ChangesetListItem | undefined>(undefined))
);

export function ChangesetsPreviewTable({ changesets, selection, loading }: IChangesetsPreviewTableProps) {
    const columns = useMemo(() => getTableColumns(), []);
    const rows = useMemo(() => (loading ? placeholderItemProvider : new ArrayItemProvider(changesets as ChangesetListItem[])), [loading, changesets]);

    if (!loading && changesets.length === 0) {
        return <div className="no-matching-changesets-message margin-top-16">{Resources.ErrorNoMatchingChangesets}</div>;
    }

    return <Table itemProvider={rows} selection={selection} columns={columns} virtualize={false /* at most 50 rows*/} />;
}

function getTableColumns() {
    return [
        new ColumnSelect(),
        {
            id: "changesetId",
            name: Resources.ChangesetIdColumnLabel,
            readonly: true,
            renderCell: renderSimpleCell,
            width: -10
        },
        {
            id: "comment",
            name: Resources.CommentColumnLabel,
            readonly: true,
            renderCell: renderSimpleCell,
            width: -40
        },
        {
            id: "checkedInBy",
            name: Resources.AuthorColumnLabel,
            readonly: true,
            renderCell: (rowIndex: number, colIndex: number, column: ITableColumn<ChangesetListItem>, item: ChangesetListItem) => (
                <SimpleTableCell key={colIndex} columnIndex={colIndex} ariaRowIndex={rowIndex} tableColumn={column}>
                    <Persona className="icon-margin" size={PersonaSize.size20} identity={item.author} />
                    {item.author.displayName}
                </SimpleTableCell>
            ),
            width: -30
        },
        {
            id: "createdDate",
            name: Resources.CreatedDateColumnLabel,
            readonly: true,
            renderCell: (rowIndex: number, colIndex: number, column: ITableColumn<ChangesetListItem>, item: ChangesetListItem) => (
                <SimpleTableCell key={colIndex} columnIndex={colIndex} ariaRowIndex={rowIndex} tableColumn={column}>
                    <Ago date={getDateInUserTimeZone(item.createdDate)} tooltipTimeFormat={tooltipTimeFormat} />
                </SimpleTableCell>
            ),
            width: -20
        }
    ];
}
